import { Autocomplete, TextField } from '@mui/material';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { ADDRESS_LOOKUP } from '../../constants/strings';
import { fastFind, retrieve } from '../../apis/address';
import { removeExtraSpaces } from '../../utils/strings';
import useBreakpoint from '../../hooks/useBreakpoint';
import { StringUtil } from '../../utils';

const AddressLookup = ({
  onChange,
  addressKey,
  countryISO,
  inputSize,
  autocompleteInputId,
  disabled,
  helperText,
  sx,
  shouldClearOnChange = false,
  ...props
}) => {
  const [lookupResults, setLookupResults] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [value, setValue] = useState({
    postcode: '',
    list: '',
  });
  const isSmallScreen = useBreakpoint('sm');
  const autocompleteRef = useRef(null);
  const lastRetrievedRef = useRef({ key: null, countryISO: null });
  const inFlightKeysRef = useRef(new Set());

  const updateLookupResults = (addressResults = []) => {
    const parsedAddressResults = addressResults.map(addressResult => ({
      key: addressResult.Key,
      list: removeExtraSpaces(addressResult.List),
      postcode: StringUtil.removeWhitespaces(addressResult.Postcode),
    }));

    setLookupResults(parsedAddressResults);
  };

  const onLookupChange = async event => {
    const lookup = event.target.value;

    debouncedFastFindLookup(lookup);
  };

  const fastFindLookup = useCallback(
    async lookup => {
      const MIN_LOOKUP_LENGTH = 3;

      if (lookup.length >= MIN_LOOKUP_LENGTH) {
        setErrorMessage('');
        try {
          const result = await fastFind({ lookup, countryISO });

          updateLookupResults(result.Item);
        } catch (error) {
          setErrorMessage(ADDRESS_LOOKUP.LOOKUP_ERROR);
        }
      } else {
        // Reset results
        setLookupResults([]);
      }
    },
    [countryISO]
  );

  const debounceWaitTime = 300;

  // Debounced version of the fastFindLookup
  const debouncedFastFindLookup = useMemo(
    () => debounce(fastFindLookup, debounceWaitTime),
    [fastFindLookup]
  );

  const cleanup = useCallback(() => {
    setValue({
      key: '',
      list: '',
      postcode: '',
    });

    setLookupResults([]);
    lastRetrievedRef.current = { key: null, countryISO: null };
    inFlightKeysRef.current.clear();
  }, []);

  const handleFocus = useCallback(() => {
    autocompleteRef &&
      isSmallScreen &&
      scrollIntoView(
        autocompleteRef.current,
        { behavior: 'smooth', block: 'center' },
        { duration: 250 }
      );
  }, [isSmallScreen]);

  const selectAddress = useCallback(
    async (key, isOptionSelectedManually = false) => {
      const cacheKey = `${countryISO}:${key}`;

      if (!key) {
        return;
      }

      if (inFlightKeysRef.current.has(cacheKey)) {
        return;
      }

      if (
        lastRetrievedRef.current.key === key &&
        lastRetrievedRef.current.countryISO === countryISO
      ) {
        return;
      }

      inFlightKeysRef.current.add(cacheKey);
      lastRetrievedRef.current = { key, countryISO };

      try {
        const fullAddressData = await retrieve({ key, countryISO });
        const fullAddress = fullAddressData?.Item?.[0];
        const cleanedPostcode = StringUtil.removeWhitespacesAndSymbols(
          fullAddress?.Postcode
        );

        if (fullAddress) {
          setValue({
            key: fullAddress.Key,
            list: removeExtraSpaces(fullAddress.List),
            postcode: cleanedPostcode,
          });

          onChange({
            ...fullAddress,
            Postcode: cleanedPostcode,
            isOptionSelectedManually,
          });
          debouncedFastFindLookup(cleanedPostcode);
        }
      } catch (error) {
        lastRetrievedRef.current = { key: null, countryISO: null };
        setErrorMessage(ADDRESS_LOOKUP.LOOKUP_ERROR);
      } finally {
        inFlightKeysRef.current.delete(cacheKey);
      }
    },
    [countryISO, debouncedFastFindLookup, onChange]
  );

  useEffect(() => {
    if (!addressKey) {
      if (value.key) {
        cleanup();
      }
      return;
    }

    if (addressKey === value.key) {
      return;
    }

    selectAddress(addressKey);
  }, [addressKey, cleanup, countryISO, selectAddress, value.key]);

  return (
    <Autocomplete
      disableClearable
      options={lookupResults}
      getOptionLabel={option => option.list}
      getOptionKey={option => option.key}
      filterOptions={options => options}
      size={inputSize}
      renderInput={params => (
        <TextField
          label={ADDRESS_LOOKUP.LABEL}
          placeholder={ADDRESS_LOOKUP.LOOKUP_PLACEHOLDER}
          onChange={onLookupChange}
          helperText={helperText || errorMessage}
          error={!!errorMessage}
          {...props}
          {...params}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              'aria-controls': `${autocompleteInputId}-listbox`,
              'data-testid': autocompleteInputId,
            },
          }}
        />
      )}
      onInputChange={(_, newInputValue) => {
        if (newInputValue === '') {
          cleanup();
          if (shouldClearOnChange) {
            onChange({});
          }
        }
      }}
      onChange={(_, selectedAddress) => {
        if (selectedAddress?.key) {
          selectAddress(selectedAddress.key, true);
        } else {
          onChange({});
          cleanup();
        }
      }}
      onFocus={handleFocus}
      value={value}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      disabled={!countryISO || disabled}
      ref={autocompleteRef}
      blurOnSelect
      sx={sx}
      slotProps={{
        popper: {
          id: `${autocompleteInputId}-listbox`,
          modifiers: [
            {
              name: 'setListboxAttributes',
              enabled: true,
              phase: 'afterWrite',
              fn({ state }) {
                state.elements.popper.setAttribute(
                  'id',
                  `${autocompleteInputId}-listbox`
                );
                state.elements.popper.setAttribute('role', 'listbox');
              },
            },
          ],
        },
      }}
    />
  );
};

AddressLookup.propTypes = {
  onChange: PropTypes.func,
  addressKey: PropTypes.string,
  countryISO: PropTypes.string,
  inputSize: PropTypes.string,
  autocompleteInputId: PropTypes.string,
  sx: PropTypes.object,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
};

export default AddressLookup;
